import { Component, inject, Input, OnChanges, OnDestroy, OnInit, signal, SimpleChanges } from '@angular/core';
import { Subscription, take } from 'rxjs';
import { CreatorResponseDto } from '../../../../common/general-types/creators.dto';
import { DeliveryAddressResponseDto } from '../../../../common/general-types/briefs.dto';
import { CreatorService } from '../../../../services/creator/creator.service';
import { ToastNotificationService } from '../../../../atoms/toast-notification/toast-notification.service';
import { MagicSearchService } from '../../service/magic-search/magic-search.service';
import { canShowCreatorDeliveryAddress } from '../creator-profile/creator-profile.helpers';

@Component({
  selector: 'mgc-delivery-address',
  imports: [],
  templateUrl: './delivery-address.component.html',
  styleUrl: './delivery-address.component.css'
})
export class MgcDeliveryAddressComponent implements OnInit, OnChanges, OnDestroy {
  @Input() creator!: CreatorResponseDto;

  defaultDeliveryAddress = signal<DeliveryAddressResponseDto | null>(null);
  addressLoadState = signal<'loading' | 'loaded' | 'error'>('loading');

  private creatorService = inject(CreatorService);
  private toastNotificationService = inject(ToastNotificationService);
  public magicSearchService = inject(MagicSearchService);
  private subscriptionList = new Subscription();

  ngOnInit(): void {
    this.getDeliveryAddresses(this.creator.blazeFirebaseUid);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['creator'] && !changes['creator'].firstChange && this.creator?.blazeFirebaseUid) {
      this.defaultDeliveryAddress.set(null);
      this.addressLoadState.set('loading');
      this.getDeliveryAddresses(this.creator.blazeFirebaseUid);
    }
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  getDeliveryAddresses(creatorId: string) {
    this.subscriptionList.add(
      this.creatorService.getCreatorDefaultDeliveryAddress(creatorId).pipe(take(1)).subscribe({
        next: (deliveryAddress) => {
          this.defaultDeliveryAddress.set(deliveryAddress ?? null);
          this.addressLoadState.set('loaded');
        },
        error: (err) => {
          console.error('Error getting delivery addresses: ', err);
          this.addressLoadState.set('error');
        }
      }),
    );
  }

  copyDeliveryAddress() {
    const address = this.defaultDeliveryAddress();
    if (!address) {
      return;
    }
    const lines = [address.fullName, address.addressLine1, address.addressLine2, `${address.town} ${address.postcode}`, address.country]
      .map((line) => line?.trim())
      .filter((line) => !!line);
    void navigator.clipboard.writeText(lines.join(', '));
    void this.toastNotificationService.alert({
      title: 'Delivery address copied to clipboard!',
      message: 'Delivery address copied to clipboard!',
      type: 'success',
      icon: 'success'
    });
  }

  allowedToShowDeliveryAddress(): boolean {
    return canShowCreatorDeliveryAddress(
      this.magicSearchService.portfolioBriefApplication(),
      this.creator.blazeFirebaseUid,
    );
  }
}
